// Copyright (c) 2025, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Grid, Paper, Typography, MenuItem, Select, Box, Tooltip, FormControl, InputLabel, Checkbox, ListItemText } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { useParams } from 'react-router-dom';
import { RESOURCE_SERVER_URLS } from '../data/configs/spl-config';
import { useGetApi } from '../data/hooks/api';
import * as Types from '../data/utils/types';
import { CsReportPDF } from '../components/CSReportPDF';
import { Download } from '@mui/icons-material';
import BarChart from "../components/CSBarChart";
import CSPieChart from '../components/CSPieChart';
import CSLineChart from "../components/CSLineChart"; 
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import '../styles/CSReport.css';

dayjs.extend(minMax);
dayjs.extend(quarterOfYear);
dayjs.extend(minMax);

type RouteParams = {
  sysId: string;
};

const getLastFourQuarters = () => {
  const quarters = [];
  let current = dayjs().startOf("quarter");
  let count = 0; 

  while (count < 4) {
    const year = current.year();
    const start = current.startOf("quarter");
    const end = current.endOf("quarter");
    if (end.isBefore(dayjs())) {
      quarters.push({
        label: `${year} Q${(start.month() / 3) + 1} - ${start.format("MMMM")} to ${end.format("MMMM")}`,
        value: `${start.format("YYYY-MM-DD")}_${end.format("YYYY-MM-DD")}`,
      });
      count++; 
    }
    current = current.subtract(3, "months"); 
  }
  return quarters;
};

const CSReport: React.FC = (): JSX.Element => {
  const { sysId: id } = useParams<RouteParams>();
  const projectSysId = id || '';
  const lastFourQuarters = getLastFourQuarters();
  const [dateRangeError, setDateRangeError] = useState<string>("");
  const [dateComparisonError, setDateComparisonError] = useState<string>("");
  const [selectedOption, setSelectedOption] = useState<string>(lastFourQuarters[0].value);
  const [from, setFrom] = useState<Dayjs | null>(dayjs(lastFourQuarters[0].value.split("_")[0]));
  const [to, setTo] = useState<Dayjs | null>(dayjs(lastFourQuarters[0].value.split("_")[1]));
  const [useCustomDates, setUseCustomDates] = useState<boolean>(false);
  const [showReport, setShowReport] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [deploymentImage, setDeploymentImage] = useState("");
  const [businessOverviewText, setBusinessOverviewText] = useState<string>("");
  const [notesImage, setNotesImage] = useState("");
  const [notesText, setNotesText] = useState<string>("");
  const businessOverviewTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const notesTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [transformedData, setTransformedData] = useState<{
    casesByDayAndState?: { day: string; opened: number; closed: number }[];
    distinctPriorities?: string[];
    casesByMonthAndPriority?: Record<number, { [key: string]: number }>;
    deploymentNames?: string[];
    casesByDeployment?: { [key: string]: number };
    distinctProductNames?: string[];
    casesByMonthAndProduct?: { [key: number]: { [key: string]: number } };
    MonthlyCases?: { yearAndMonth: string; incidentCount: number; queryCount: number }[];
  } | null>(null);  
  
  const csReportDetailsUrl = `${RESOURCE_SERVER_URLS[0]}/report\-details?projectSysId=${projectSysId}&from=${from?.format('YYYY-MM-DD')}&to=${to?.format('YYYY-MM-DD')}`;
  const { data: csReportData, getApiData: getReportData } = useGetApi<Types.CSReportDetailsResponse>({
    url: csReportDetailsUrl,
    headers: { accept: "application/json" },
  });
  
  const handleOptionChange = (event: any) => {
    const value = event.target.value;
    setSelectedOption(value);
    if (value === "custom") {
      const currentDate = dayjs(); 
      setFrom(currentDate);  
      setTo(currentDate); 
      setUseCustomDates(true);
    } else {
      setUseCustomDates(false);
      const [start, end] = value.split("_");
      setFrom(dayjs(start));
      setTo(dayjs(end));
    }
  };

  const handleTextareaChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (businessOverviewTextareaRef.current) {
      const cursorPosition = businessOverviewTextareaRef.current.selectionStart;
      setBusinessOverviewText(e.target.value);
      setTimeout(() => {
        if (businessOverviewTextareaRef.current) {
          businessOverviewTextareaRef.current.setSelectionRange(cursorPosition, cursorPosition);
          businessOverviewTextareaRef.current.focus();
        }
      }, 0);
    }
  }, []);

  const handleNotesTextareaChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (notesTextareaRef.current) {
      const cursorPosition = notesTextareaRef.current.selectionStart;
      setNotesText(e.target.value);
      setTimeout(() => {
        if (notesTextareaRef.current) {
          notesTextareaRef.current.setSelectionRange(cursorPosition, cursorPosition);
          notesTextareaRef.current.focus();
        }
      }, 0);
    }
  }, []);

  const handleImageUpload = (
    event: React.ChangeEvent<HTMLInputElement>, 
    section: 'deployment' | 'notes'
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.src = reader.result as string;
        img.onload = () => {
          const TARGET_WIDTH = 794;
          const TARGET_HEIGHT = 500;

          let width = img.width;
          let height = img.height;

          const scaleFactor = Math.min(
            TARGET_WIDTH / width, 
            TARGET_HEIGHT / height
          );

          width = width * scaleFactor;
          height = height * scaleFactor;

          const canvas = document.createElement("canvas");
          canvas.width = TARGET_WIDTH;
          canvas.height = TARGET_HEIGHT;

          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.fillStyle = "#FFFFFF";
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            const offsetX = (TARGET_WIDTH - width) / 2;
            const offsetY = (TARGET_HEIGHT - height) / 2;
            ctx.drawImage(img, offsetX, offsetY, width, height);

            const resizedImage = canvas.toDataURL("image/jpeg");

            if (section === 'deployment') {
              setDeploymentImage(resizedImage);
            } else if (section === 'notes') {
              setNotesImage(resizedImage);
            }
          }
        };
      };
      reader.readAsDataURL(file);
    }
  };   

const handleDownloadPDF = () => {
  CsReportPDF(
      'cs-report',
      csReportData?.subscriptionDetails?.accountName ?? 'Unknown Account',
      csReportData?.subscriptionDetails?.projectKey ?? 'Unknown Project Key',
      csReportData?.slaDetails?.slaRecords ?? [],
      from,
      to,
      notesText,
      notesImage , 
      deploymentImage,
      businessOverviewText
  );
};

const handleSubmit = () => {
  if (useCustomDates && from && to) {
    const diffInMonths = to.diff(from, 'month');
    if (diffInMonths > 6) {
      setDateRangeError("The date range cannot exceed 6 months.");
      return; 
    }

    if (from.isAfter(to)) {
      setDateComparisonError("The 'From' date must be earlier than the 'To' date.");
      return; 
    }
    
  } 
    setDateComparisonError("");
    setDateRangeError(""); 
    setIsSubmitted(true);
    setShowReport(true);
    getReportData(csReportDetailsUrl);
 };

useEffect(() => {
    if (!csReportData || !Array.isArray(csReportData.casesRecords)) {
        return;
    }
    
    const filteredCaseRecords = csReportData.casesRecords.filter(
        (record) => record.caseType === "Incident" || record.caseType === "Query"
    );

    const casesByDayAndState = filteredCaseRecords.reduce(
        (dayWiseCases: { day: string; opened: number; closed: number }[], record) => {
            const openedDate = record.opened.split(" ")[0]; 
            const caseState = record.caseState;

            let dayStateCount = dayWiseCases.find((entry) => entry.day === openedDate);
            if (!dayStateCount) {
                dayStateCount = { day: openedDate, opened: 0, closed: 0 };
                dayWiseCases.push(dayStateCount);
            }

            if (caseState === "Open") {
                dayStateCount.opened++;
            } else if (caseState === "Closed") {
                dayStateCount.closed++;
            }

            return dayWiseCases;
        },
        []
    );

    const casesByDeployment = filteredCaseRecords.reduce(
        (casesPerDeployment: { [key: string]: number }, record) => {
            if (!record.deployment) return casesPerDeployment;
            casesPerDeployment[record.deployment] = (casesPerDeployment[record.deployment] || 0) + 1;
            return casesPerDeployment;
        },
        {}
    );

    const deploymentNames = Object.keys(casesByDeployment);

    const distinctPriorities = Array.from(new Set(filteredCaseRecords.map((record) => record.casePriority)));

    const casesByMonthAndPriority = filteredCaseRecords.reduce(
        (monthlyPriorityCases: { [key: number]: { [key: string]: number } }, record) => {
            const month = new Date(record.opened).getMonth();
            const priority = record.casePriority;

            if (!monthlyPriorityCases[month]) {
                monthlyPriorityCases[month] = {};
            }
            if (!monthlyPriorityCases[month][priority]) {
                monthlyPriorityCases[month][priority] = 0;
            }
            monthlyPriorityCases[month][priority]++;
            return monthlyPriorityCases;
        },
        {}
    );

    const distinctProductNames = Array.from(new Set(filteredCaseRecords.map((record) => record.productName)));

    const casesByMonthAndProduct = filteredCaseRecords.reduce(
        (monthlyProductCases: { [key: number]: { [key: string]: number } }, record) => {
            const month = new Date(record.opened).getMonth();
            const product = record.productName;

            if (!monthlyProductCases[month]) {
                monthlyProductCases[month] = {};
            }
            if (!monthlyProductCases[month][product]) {
                monthlyProductCases[month][product] = 0;
            }
            monthlyProductCases[month][product]++;
            return monthlyProductCases;
        },
        {}
    );

    const MonthlyCases = csReportData.monthlyCounts.slice().reverse().map((entry) => ({
      yearAndMonth: entry.yearAndMonth,
      incidentCount: entry.counts.incidentCount || 0,
      queryCount: entry.counts.queryCount || 0,
     }));

    setTransformedData({
        casesByDayAndState,
        casesByDeployment,
        deploymentNames,
        casesByMonthAndPriority,
        distinctPriorities,
        casesByMonthAndProduct,
        distinctProductNames,
        MonthlyCases,
    });
}, [csReportData]);

  const FormView = () => (
    <div className="form-container blurred-background">
      <Paper className="centered-form" elevation={3} sx={{ padding: '20px', width: '400px', margin: '0 auto' }}>
        <Typography gutterBottom variant="h4" component="div" sx={{ textAlign: 'center', marginBottom: '20px' }}>
          CS Report
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ marginBottom: '20px', textAlign: 'center' }}>
          Please select the date range for the CS Report
        </Typography>
          <FormControl fullWidth sx={{ marginBottom: "20px" }}>
          <Select value={selectedOption} onChange={handleOptionChange}>
            {lastFourQuarters.map((quarter) => (
              <MenuItem key={quarter.value} value={quarter.value}>
                {quarter.label}
              </MenuItem>
            ))}
            <MenuItem value="custom">Custom Date Range</MenuItem>
          </Select>
        </FormControl>
            {dateRangeError && (
              <Typography variant="body2" color="error" sx={{ marginBottom: '10px', textAlign: 'center' }}>
                 {dateRangeError}
              </Typography>
            )}

            {dateComparisonError && (
              <Typography variant="body2" color="error" sx={{ marginBottom: '10px', textAlign: 'center' }}>
                 {dateComparisonError}
              </Typography>
            )}

           {useCustomDates && (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
            <Grid item xs={6}>
              <DatePicker
                label="From"
                value={from}
                onChange={(newValue) => setFrom(newValue)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={6}>
              <DatePicker
                label="To"
                value={to}
                onChange={(newValue) => setTo(newValue)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
           )}
        <Button variant="contained" className="submitButton" onClick={handleSubmit}>
          Submit
        </Button>
      </Paper>
    </div>
  );

  const MainView = () => {
    return (
      <>
        {(
        <Box id="downloadReportButton" flex={2}>
          <Tooltip title="Download Report">
            <Button
              variant="contained"
              className="downloadButton"
              onClick={handleDownloadPDF}
            >
              <Download />
            </Button>
          </Tooltip>
        </Box>
        )}

        <div id="cs-report">
          <div className="main-view-container">
            <h1 className="main-view-header">Customer Success Report for {csReportData?.subscriptionDetails?.projectName}</h1>
            <div className="subscription-widget">
              <h2 className="widget-title">Subscription Details</h2>
              <div className="subscription-details">
                 <div className="detail-item">
                    <strong>Project Name</strong> 
                     <div className="data-info">{csReportData?.subscriptionDetails?.projectName || '-'}</div>
                </div>
                <div className="detail-item">
                    <strong>Project Key</strong>
                    <div className="data-info">{csReportData?.subscriptionDetails?.projectKey || '-'}</div>
                </div>
                <div className="detail-item">
                    <strong>Project Type</strong>
                    <div className="data-info">{csReportData?.subscriptionDetails?.projectType || '-'}</div>
                </div>
                <div className="detail-item">
                    <strong>Subscription Start Date</strong>
                    <div className="data-info">{csReportData?.subscriptionDetails?.startDate || '-'}</div>
                </div>
                <div className="detail-item">
                    <strong>Subscription End Date</strong>
                    <div className="data-info">{csReportData?.subscriptionDetails?.endDate || '-'}</div>
                </div> 
                <div className="detail-item">
                    <strong>Support Tier</strong>
                    <div className="data-info">{csReportData?.subscriptionDetails?.supportTier || '-'}</div>
                </div>
              </div>

              <div className="price-table">
              <table className="table-container">
                <thead>
                  <tr className="table-header">
                    <th>Product(s)</th>
                    <th>Subscription</th>
                    <th>Query Hours</th>
                  </tr>
                </thead>
                <tbody className="table-data">
                  {(() => {
                    const allProducts = Object.values(csReportData?.projectDeployments || {})
                      .flatMap((deployment) => (deployment as { products: any[] }).products || []);
                      
                    const uniqueProductsMap = allProducts.reduce((acc, product) => {
                      if (!acc.has(product.name)) {
                        acc.set(product.name, product);
                      }
                      return acc;
                    }, new Map());
                    
                    const uniqueProducts = Array.from(uniqueProductsMap);
                    const rowSpanCount = uniqueProducts.length;
                    
                    return (uniqueProducts as [string, any][]).map(([name, product], index) => (
                      <tr key={name}>
                        <td>{name}</td>
                        {index === 0 && (
                          <>
                            <td rowSpan={rowSpanCount}>
                              ${csReportData?.subscriptionDetails?.subscription}
                            </td>
                            <td rowSpan={rowSpanCount}>
                              {csReportData?.subscriptionDetails?.totalQueryHours} <br />
                              ({csReportData?.subscriptionDetails?.consumedQueryHours} Utilized)
                            </td>
                          </>
                        )}
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
            </div>
            <br />
            <div className="current-products-widget">
              {Object.values(csReportData?.projectDeployments || {})
                .flatMap((deployment) => (deployment as { products: any[] }).products || []).length > 0 && (
                <>
                  <h2 className="widget-title">Usage</h2>
                  <div className="products-table-container">
                    <table className="table-container">
                      <thead>
                        <tr className="product-table-header">
                          {Object.values(csReportData?.projectDeployments || {})
                            .flatMap((deployment) => (deployment as { products: any[] }).products  || [])
                            .map((product, index) => (
                              <th key={index}>{product.cores || "-" } Cores</th>
                            ))}
                        </tr>
                      </thead>
                      <tbody className="product-table-data">
                        <tr>
                          {Object.values(csReportData?.projectDeployments || {})
                            .flatMap((deployment) => (deployment as { products: any[] }).products  || [])
                            .map((product, index) => (
                              <td key={index}>{product.name}</td>
                            ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
            <br />
            <div className="product-versions-widget">
                {csReportData?.projectDeployments?.some(
                  (deployment: Types.ProjectDeployment) => deployment.products && deployment.products.length > 0
                ) && (
                  <>
                  <h2 className="widget-title">Product Versions and EOL Status</h2>
                    {csReportData?.projectDeployments?.map((deployment, index) => {
                      const { name: deploymentName, products } = deployment;
                      return (
                        products.length > 0 && (
                          <div key={index} className="deployment-section">
                            <h3 className="table-title">{deploymentName} Environment</h3>
                            <table className="table-container">
                              <thead>
                                <tr className="table-header">
                                  <th>Product</th>
                                  <th>Earliest Possible Support EOL Date</th>
                                  <th>Support EOL Date</th>
                                  <th>Current Support Status</th>
                                  <th>Comments</th>
                                </tr>
                              </thead>
                              <tbody className="table-data">
                                {products.map((product, index) => (
                                  <tr key={index}>
                                    <td>{product.name} v{product.version} </td>
                                    <td>{product.earliestPossibleSupportEOLDate || "N/A"}</td>
                                    <td>{product.eolDate || "N/A"}</td>
                                    <td>{product.supportStatus || "N/A"}</td>
                                    <td>{" "}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )
                      );
                    })}
                  </>
                )}
              </div>
            <br />
            <div className="cases-charts-widget">
              <h2 className="widget-title">Cases </h2>
              <div className='charts'>

                <div className="opened-resolved-bar-chart">
                  {transformedData?.casesByDayAndState ? (
                    <BarChart 
                      data={transformedData?.casesByDayAndState} 
                      chartType="state" 
                      from={from ?? dayjs()}  
                      to={to ?? dayjs()}
                    />
                  ) : (
                    <p>No data available for opened/resolved cases.</p>
                  )}
                </div>

                <div className="environment-chart">
                  <CSPieChart 
                    deploymentNames={transformedData?.deploymentNames || []}
                    data={transformedData?.casesByDeployment || {}}
                  />
                </div>

                <div className="product-bar-chart">
                  {transformedData?.casesByMonthAndProduct && transformedData?.distinctProductNames ? (
                    <BarChart
                      data={transformedData?.casesByMonthAndProduct}
                      distinctProductNames={transformedData?.distinctProductNames}
                      from={from ?? dayjs()} 
                      to={to ?? dayjs()}
                      chartType="product"
                    />
                  ) : (
                    <p>No data available for product cases.</p>
                  )}
                </div> 

                <div className="priority-bar-chart">
                  {transformedData?.casesByMonthAndPriority && transformedData?.distinctPriorities ? (
                    <BarChart
                      data={transformedData?.casesByMonthAndPriority}
                      distinctPriorities={transformedData?.distinctPriorities}
                      from={from ?? dayjs()}  
                      to={to ?? dayjs()}
                      chartType="priority"
                    />
                  ) : (
                    <p>No data available for priority cases.</p>
                  )}
                </div>
              </div>
            </div>              
            <br />
            <div className="cases-volume-widget">
              <h2 className="widget-title">Case Volume by Months</h2>
              <div className="chart-container">
                  {transformedData?.MonthlyCases && transformedData?.MonthlyCases.length > 0 ? (
                      <CSLineChart
                          data={transformedData.MonthlyCases}
                          from={from ?? dayjs().startOf("month")}  
                          to={to ?? dayjs().endOf("month")} 
                      />
                  ) : (
                      <p style={{ textAlign: 'center', color: 'red' }}>No data available for case volume by quarters.</p>
                  )}
              </div>
              </div>
          <br/>
         <div className="incident-sla-compliance-widget">
              <h2 className="widget-title">Incident SLA Compliance </h2>
              {csReportData?.slaDetails?.slaPerformanceStats ? (
                <div
                  className="sla-stats"
                  style={{ display: "flex", flexDirection: "row", justifyContent: "space-around" }}
                >
                  <div className="sla-metric">
                    <h3>{csReportData.slaDetails.slaPerformanceStats.Resolution.percentage}%</h3>
                    <p>
                      Resolution ({csReportData.slaDetails.slaPerformanceStats.Resolution.fraction})
                    </p>
                  </div>
                  <div className="sla-metric">
                    <h3>{csReportData.slaDetails.slaPerformanceStats.Workaround.percentage}%</h3>
                    <p>
                      Workaround ({csReportData.slaDetails.slaPerformanceStats.Workaround.fraction})
                    </p>
                  </div>
                  <div className="sla-metric">
                    <h3>{csReportData.slaDetails.slaPerformanceStats.Response.percentage}%</h3>
                    <p>
                      Response ({csReportData.slaDetails.slaPerformanceStats.Response.fraction})
                    </p>
                  </div>
                </div>
              ) : (
                <p>No SLA statistics available</p>
              )}
            </div>
            <br/>
            <div className="product-updates-widget">
              {csReportData?.projectDeployments?.some((deployment) =>
                deployment.products?.some((product) => product.updateLevelInfo != null)
              ) && (
                <>
                  <h2 className="widget-title">Product Update Levels </h2>
                  {csReportData?.projectDeployments?.map((deployment, index) => {
                    const productsWithUpdateInfo = deployment.products?.filter(
                      (product) => product.updateLevelInfo != null
                    );

                    return productsWithUpdateInfo.length > 0 ? (
                      <div key={index} className="environment-table-container">
                        <h3 className="table-title">{deployment.name} Environment</h3>
                        <table className="table-container">
                          <thead className="table-header">
                            <tr>
                              <th>Product</th>
                              <th>Current Update Level</th>
                            </tr>
                          </thead>
                          <tbody className="table-data">
                            {productsWithUpdateInfo.map((product, productIndex) => (
                              <tr key={productIndex}>
                                <td>
                                  {product.name} v{product.version}
                                </td>
                                <td>{product.updateLevelInfo ?? "N/A"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : null;
                  })}
                </>
              )}
            </div>
            <br />
            <div className="engagements-widget">
            <h2 className="widget-title">Engagements</h2>
            <table className="table-container">
              <thead>
                <tr className="table-header">
                  <th>Created</th>
                  <th>Case</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody className="table-data">
                {csReportData?.casesRecords && csReportData.casesRecords.length > 0 ? (
                  (() => {
                    const engagementCases = csReportData.casesRecords.filter(
                      (record) => record.caseType === "Engagement"
                    );

                    return engagementCases.length > 0 ? (
                      engagementCases.map((record, index) => (
                        <tr key={index}>
                          <td>{record.opened || "N/A"}</td>
                          <td>{record.caseNumber || "N/A"}</td>
                          <td>{record.description || "N/A"}</td>
                          <td>{record.engagementType || "N/A"}</td>
                          <td>{record.updated || "N/A"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5}>
                          No Engagement Data Available
                        </td>
                      </tr>
                    );
                  })()
                ) : (
                  <tr>
                    <td colSpan={5} >
                      No Engagement Data Available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <br />
            <div className="deployment-image-section">
              <h3 className="widget-title">Deployment</h3>
              {!deploymentImage ? (
                <input
                  type="file"
                  className="image-input"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, "deployment")}
                />
              ) : (
                <div className="image-preview">
                  <img src={deploymentImage} alt="Deployment Preview" />
                </div>
              )}
            </div>
          <br />
              <div className="business-overview-section">
              <h2 className="widget-title">Business Overview</h2>
              <textarea
                ref={businessOverviewTextareaRef}
                className="paragraph-input"
                placeholder="Enter details"
                rows={6}
                value={businessOverviewText}
                onChange={handleTextareaChange}
              ></textarea>
            </div>
          <br />
            <div className="extra-notes-section">
              <h2 className="widget-title">Notes</h2>
              {!notesImage ? (
                <input
                  type="file"
                  className="image-input"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, "notes")}
                />
              ) : (
                <div className="image-preview">
                  <img src={notesImage} alt="Extra Image Preview" />
                </div>
              )}
              <textarea
                ref={notesTextareaRef}
                className="extra-notes-input"
                placeholder="Enter details"
                rows={6}
                value={notesText}
                onChange={handleNotesTextareaChange}
              ></textarea>
            </div>
          </div>
        </div>
      </>
    );
};

  return <>{showReport ? <MainView /> : <FormView />}</>;
};

export default CSReport;
